import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MapPin, AlertTriangle, LogOut, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Row {
  id: string; trip_code: string; status: string;
  client_name: string | null;
  pickup_city: string; delivery_city: string;
  rate_sar: string; container_type: string | null; container_number: string | null;
  scheduled_pickup_at: string | null; scheduled_delivery_at: string | null;
  expected_distance_km: number | null;
}

const STATUS_COLOR: Record<string, string> = {
  assigned: 'border-sky-500/40 bg-sky-500/5',
  en_route_to_pickup: 'border-amber-500/40 bg-amber-500/5',
  at_pickup: 'border-amber-500/40 bg-amber-500/5',
  loaded: 'border-amber-500/40 bg-amber-500/5',
  in_transit: 'border-violet-500/40 bg-violet-500/5',
  at_delivery: 'border-amber-500/40 bg-amber-500/5',
  unloaded: 'border-amber-500/40 bg-amber-500/5',
  returning: 'border-amber-500/40 bg-amber-500/5',
  completed: 'border-emerald-500/40 bg-emerald-500/5',
};

const ACTIVE_STATUSES = ['assigned','en_route_to_pickup','at_pickup','loaded','in_transit','at_delivery','unloaded','returning'];

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function MyFlatbedTripsPage() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'today' | 'week' | 'completed'>('today');

  const refresh = useCallback(async () => {
    try {
      // Resolve driver via /me/dashboard endpoint (PR P3) which matches by phone
      let driverId: string | null = null;
      try {
        const r = await apiClient.get<{ driver: { id: string } }>('/api/v1/driver-portal/me/dashboard');
        driverId = r.data.driver?.id ?? null;
      } catch { /* not a driver-linked account; fall back to all flatbed trips list */ }
      const params = new URLSearchParams();
      if (driverId) params.set('driverId', driverId);
      params.set('limit', '200');
      const r = await apiClient.get<Row[]>('/api/v1/flatbed/trips?' + params.toString());
      setRows(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = rows.filter((r) => {
    if (tab === 'today') {
      if (!r.scheduled_pickup_at) return ACTIVE_STATUSES.includes(r.status);
      const d = new Date(r.scheduled_pickup_at);
      const now = new Date();
      return d.toDateString() === now.toDateString() || ACTIVE_STATUSES.includes(r.status);
    }
    if (tab === 'week') {
      if (!r.scheduled_pickup_at) return false;
      const d = new Date(r.scheduled_pickup_at);
      const diff = (d.getTime() - Date.now()) / 86400000;
      return diff >= -1 && diff <= 7;
    }
    return r.status === 'completed' || r.status === 'invoiced' || r.status === 'paid';
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">My trips</div>
            <div className="text-xs text-muted-foreground">{user?.fullNameEn ?? user?.code ?? 'Driver'}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="gap-2"><LogOut className="h-4 w-4" /> Sign out</Button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-3">
        <div className="flex gap-2 border-b border-border">
          {(['today','week','completed'] as const).map((v) => (
            <button key={v} onClick={() => setTab(v)}
              className={'px-3 py-2 text-sm border-b-2 transition-colors -mb-px capitalize ' + (tab === v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {v}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No trips here.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map((t) => (
              <Link key={t.id} to={'/dashboard/flatbed/my-trips/' + t.id}
                className={'block rounded-lg border p-3 hover:bg-muted/30 transition-colors ' + (STATUS_COLOR[t.status] ?? 'border-border')}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-mono text-xs">{t.trip_code}</div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-background/40 border border-border">{t.status.replace(/_/g, ' ')}</span>
                </div>
                <div className="text-sm font-medium mt-1 inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {t.pickup_city} → {t.delivery_city}</div>
                {t.client_name && <div className="text-xs text-muted-foreground mt-0.5">{t.client_name}</div>}
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                  {t.container_type && <span>{t.container_type}{t.container_number ? ' · ' + t.container_number : ''}</span>}
                  {t.scheduled_pickup_at && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(t.scheduled_pickup_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                  {t.expected_distance_km && <span>{t.expected_distance_km} km</span>}
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Rate {fmtSar(Number(t.rate_sar))}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

